import React from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import {
  CheckCircle,
  Delete,
  DragIndicator,
  ErrorOutline,
  UploadFileOutlined,
} from "@mui/icons-material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import __ from "src/utils/Translations";

interface SortableFileItemProps {
  id: string;
  file: File;
  isUploading: boolean;
  isValid: boolean;
  index: number;
  errorMessage?: string;
  onRemove: (id: string, index: number) => void;
  size?: string;
}

const formatFileSize = (size: number | string): string => {
  if (typeof size === "string") return size;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const SortableFileItem: React.FC<SortableFileItemProps> = ({
  id,
  file,
  isUploading,
  isValid,
  index,
  errorMessage,
  onRemove,
  size = "",
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "transform 50ms ease, opacity 200ms ease",
    opacity: isDragging ? 0.5 : 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: { md: "20px 16px" },
    cursor: "default",
  };
  const formattedSize = formatFileSize(file.size > 0 ? file.size : size);

  return (
    <Box ref={setNodeRef} sx={style}>
      <IconButton {...listeners} {...attributes} sx={{ cursor: "grab" }}>
        <DragIndicator />
      </IconButton>

      <UploadFileOutlined
        sx={{ margin: "8px" }}
        color={isValid ? "primary" : "error"}
      />

      <Box flex={1}>
        <Typography
          variant="body2"
          sx={{
            color: isValid ? "inherit" : (theme) => theme.palette.error.main,
          }}
        >
          {file.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: isValid ? "inherit" : (theme) => theme.palette.error.main,
          }}
        >
          {`${formattedSize} `}
          {/* eslint-disable-next-line no-nested-ternary */}
          {isUploading
            ? __("Wczytywanie")
            : isValid
            ? __("Ukończono")
            : `${__("Wystąpił błąd")} - ${errorMessage}`}
        </Typography>
      </Box>

      {!isUploading && isValid && (
        <CheckCircle sx={{ color: (theme) => theme.palette.success.main }} />
      )}
      {!isUploading && !isValid && (
        <ErrorOutline sx={{ color: (theme) => theme.palette.error.main }} />
      )}

      <IconButton
        onClick={() => onRemove(id, index)}
        sx={{ marginLeft: "8px", cursor: "pointer" }}
      >
        <Delete />
      </IconButton>
      {isUploading && <CircularProgress size={40} />}
    </Box>
  );
};
export default SortableFileItem;
